"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.onboardingOpenApiDocs = void 0;
// Standard error response schema with error_code support
const ErrorResponse = {
    type: 'object',
    properties: {
        success: {
            type: 'boolean',
            example: false
        },
        error: {
            type: 'string',
            description: 'Human-readable error message'
        },
        error_code: {
            type: 'integer',
            description: 'Numeric error code for programmatic handling'
        }
    }
};
const OnboardingCreatePayload = {
    type: 'object',
    properties: {
        profession_id: {
            type: 'string',
            description: 'Associated profession ID',
            required: true
        },
        avatar_id: {
            type: 'string',
            description: 'Associated avatar ID (optional, avatar will be created if not provided)',
            required: false
        },
        status: {
            type: 'string',
            enum: ['in_progress', 'completed', 'abandoned'],
            description: 'Onboarding status',
            required: false,
            default: 'in_progress'
        },
        current_step: {
            type: 'number',
            description: 'Current step index',
            required: false,
            default: 0
        },
        completed_steps: {
            type: 'array',
            description: 'Array of completed step indices',
            required: false,
            items: {
                type: 'number'
            }
        },
        data: {
            type: 'object',
            description: 'Onboarding data including answers',
            required: false,
            properties: {
                answers: {
                    type: 'object',
                    description: 'Raw answers by question ID - key is question ID, value depends on question type',
                    additionalProperties: true,
                    example: {
                        "question_id_text": "Free text answer",
                        "question_id_number": 25,
                        "question_id_select": "selected_option_value",
                        "question_id_radio": "selected_radio_value",
                        "question_id_checkbox": ["option1", "option2", "option3"]
                    },
                    properties: {
                        "question_id": {
                            oneOf: [
                                {
                                    type: "string",
                                    description: "Answer for text, textarea, select, or radio questions"
                                },
                                {
                                    type: "number",
                                    description: "Answer for number questions"
                                },
                                {
                                    type: "array",
                                    description: "Answer for checkbox questions (multiple selections)",
                                    items: {
                                        type: "string"
                                    }
                                }
                            ]
                        }
                    }
                },
                structured_answers: {
                    type: 'object',
                    description: 'Answers organized by step and question - structured_answers[step.id][question.id] = { question_title, question_type, answer }',
                    additionalProperties: {
                        type: 'object',
                        description: 'Step ID as key, contains questions for this step',
                        additionalProperties: {
                            type: 'object',
                            description: 'Question ID as key, contains question details and answer',
                            properties: {
                                question_title: { type: 'string', description: 'Title of the question' },
                                question_type: {
                                    type: 'string',
                                    enum: ['text', 'textarea', 'number', 'select', 'radio', 'checkbox'],
                                    description: 'Type of the question'
                                },
                                answer: {
                                    description: 'Answer value - type depends on question type',
                                    oneOf: [
                                        { type: 'string', description: 'For text, textarea, select, radio questions' },
                                        { type: 'number', description: 'For number questions' },
                                        { type: 'array', items: { type: 'string' }, description: 'For checkbox questions' }
                                    ]
                                }
                            }
                        }
                    },
                    example: {
                        "step_id": {
                            "question_id": {
                                "question_title": "What is your experience?",
                                "question_type": "textarea",
                                "answer": "I have 5 years of experience in..."
                            }
                        }
                    },
                },
                steps: {
                    type: 'array',
                    description: 'Steps information',
                    items: {
                        type: 'object',
                        properties: {
                            id: { type: 'number' },
                            title: { type: 'string' },
                            order: { type: 'number' }
                        }
                    }
                },
                completion_date: {
                    type: 'string',
                    format: 'date-time',
                    description: 'Date when onboarding was completed'
                }
            }
        },
        knowledge_ids: {
            type: 'array',
            description: 'Array of knowledge entry IDs',
            required: false,
            items: {
                type: 'string'
            }
        },
        avatar_config: {
            type: 'object',
            description: 'Avatar configuration - selectedModel is REQUIRED for avatar creation on completion',
            required: false,
            properties: {
                name: { type: 'string', description: 'Avatar name' },
                age: { type: 'string', description: 'Avatar age' },
                gender: {
                    type: 'string',
                    enum: ['male', 'female'],
                    description: 'Avatar gender'
                },
                voice: { type: 'string', description: 'Azure voice name (e.g. pl-PL-AgnieszkaNeural)' },
                description: { type: 'string', description: 'Avatar description', required: true },
                selectedModel: {
                    type: 'object',
                    description: 'REQUIRED for avatar creation - Selected AI model configuration',
                    required: true,
                    properties: {
                        model: {
                            type: 'object',
                            required: true,
                            properties: {
                                label: { type: 'string', description: 'Display name of the model' },
                                value: { type: 'string', description: 'API identifier for the model' }
                            }
                        },
                        cost: { type: 'number', description: 'Cost per token' }
                    }
                }
            }
        },
        logo: {
            type: 'object',
            description: 'Avatar image/logo file',
            required: false
        }
    }
};
const OnboardingUpdatePayload = {
    type: 'object',
    properties: {
        profession_id: {
            type: 'string',
            description: 'Associated profession ID',
            required: false
        },
        avatar_id: {
            type: 'string',
            description: 'Associated avatar ID',
            required: false
        },
        status: {
            type: 'string',
            enum: ['in_progress', 'completed', 'abandoned'],
            description: 'Onboarding status',
            required: false
        },
        current_step: {
            type: 'number',
            description: 'Current step index',
            required: false
        },
        completed_steps: {
            type: 'array',
            description: 'Array of completed step indices',
            required: false,
            items: {
                type: 'number'
            }
        },
        data: {
            type: 'object',
            description: 'Onboarding data including answers',
            required: false,
            properties: {
                answers: {
                    type: 'object',
                    description: 'Raw answers by question ID - supports text, number, array (for checkbox), etc.',
                    additionalProperties: true,
                    example: {
                        "question_id_1": "Text answer",
                        "question_id_2": 25,
                        "question_id_3": ["option1", "option2"],
                        "question_id_4": "selected_option"
                    }
                },
                structured_answers: {
                    type: 'object',
                    description: 'Answers organized by step and question - structured_answers[step.id][question.id] = { question_title, question_type, answer }',
                    additionalProperties: {
                        type: 'object',
                        description: 'Step ID as key, contains questions for this step',
                        additionalProperties: {
                            type: 'object',
                            description: 'Question ID as key, contains question details and answer',
                            properties: {
                                question_title: { type: 'string', description: 'Title of the question' },
                                question_type: {
                                    type: 'string',
                                    enum: ['text', 'textarea', 'number', 'select', 'radio', 'checkbox'],
                                    description: 'Type of the question'
                                },
                                answer: {
                                    description: 'Answer value - type depends on question type',
                                    oneOf: [
                                        { type: 'string', description: 'For text, textarea, select, radio questions' },
                                        { type: 'number', description: 'For number questions' },
                                        { type: 'array', items: { type: 'string' }, description: 'For checkbox questions' }
                                    ]
                                }
                            }
                        }
                    }
                },
                steps: {
                    type: 'array',
                    description: 'Steps information',
                    items: {
                        type: 'object',
                        properties: {
                            id: { type: 'number' },
                            title: { type: 'string' },
                            order: { type: 'number' }
                        }
                    }
                },
                completion_date: {
                    type: 'string',
                    format: 'date-time',
                    description: 'Date when onboarding was completed'
                }
            }
        },
        knowledge_ids: {
            type: 'array',
            description: 'Array of knowledge entry IDs',
            required: false,
            items: {
                type: 'string'
            }
        },
        avatar_config: {
            type: 'object',
            description: 'Avatar configuration',
            required: false,
            properties: {
                name: { type: 'string', description: 'Avatar name' },
                age: { type: 'string', description: 'Avatar age' },
                gender: {
                    type: 'string',
                    enum: ['male', 'female'],
                    description: 'Avatar gender'
                },
                voice: { type: 'string', description: 'Azure voice name for text-to-speech' },
                description: { type: 'string', description: 'Avatar description' },
                selectedModel: {
                    type: 'object',
                    description: 'Selected AI model configuration',
                    properties: {
                        model: {
                            type: 'object',
                            properties: {
                                label: { type: 'string' },
                                value: { type: 'string' }
                            }
                        },
                        cost: { type: 'number' }
                    }
                }
            }
        },
        logo: {
            type: 'object',
            description: 'Avatar image/logo file',
            required: false
        }
    }
};
const OnboardingResponse = {
    type: 'object',
    properties: {
        id: {
            type: 'number'
        },
        user: {
            type: 'object',
            properties: {
                id: { type: 'number' },
                name: { type: 'string' },
                username: { type: 'string' },
                email: { type: 'string' },
                firstname: { type: 'string' },
                lastname: { type: 'string' }
            }
        },
        profession: {
            type: 'object',
            properties: {
                id: { type: 'number' },
                name: { type: 'string' },
                description: { type: 'string' },
                branch: {
                    type: 'object',
                    properties: {
                        id: { type: 'number' },
                        name: { type: 'string' }
                    }
                },
                image: {
                    type: 'object',
                    properties: {
                        id: { type: 'number' },
                        filename: { type: 'string' },
                        url: { type: 'string' }
                    }
                },
                knowledgeEntries: {
                    type: 'array',
                    items: {
                        type: 'object',
                        properties: {
                            id: { type: 'number' },
                            title: { type: 'string' }
                        }
                    }
                },
                steps: {
                    type: 'array',
                    items: {
                        type: 'object',
                        properties: {
                            id: { type: 'number' },
                            title: { type: 'string' },
                            order: { type: 'number' }
                        }
                    }
                }
            }
        },
        avatar: {
            type: 'object',
            properties: {
                id: { type: 'number' },
                name: { type: 'string' },
                description: { type: 'string' }
            }
        },
        status: {
            type: 'string',
            enum: ['in_progress', 'completed', 'abandoned']
        },
        current_step: {
            type: 'number'
        },
        completed_steps: {
            type: 'array',
            items: {
                type: 'number'
            }
        },
        data: {
            type: 'object',
            properties: {
                answers: {
                    type: 'object',
                    description: 'Question answers by question ID - key is question ID, value depends on question type',
                    additionalProperties: true,
                    example: {
                        "question_id_text": "User provided text answer",
                        "question_id_number": 42,
                        "question_id_select": "option_value",
                        "question_id_radio": "selected_value",
                        "question_id_checkbox": ["value1", "value2"]
                    }
                },
                structured_answers: {
                    type: 'object',
                    description: 'Answers organized by step and question - structured_answers[step.id][question.id] = { question_title, question_type, answer }',
                    additionalProperties: {
                        type: 'object',
                        description: 'Step ID as key, contains questions for this step',
                        additionalProperties: {
                            type: 'object',
                            description: 'Question ID as key, contains question details and answer',
                            properties: {
                                question_title: { type: 'string', description: 'Title of the question' },
                                question_type: {
                                    type: 'string',
                                    enum: ['text', 'textarea', 'number', 'select', 'radio', 'checkbox'],
                                    description: 'Type of the question'
                                },
                                answer: {
                                    description: 'Answer value - type depends on question type',
                                    oneOf: [
                                        { type: 'string', description: 'For text, textarea, select, radio questions' },
                                        { type: 'number', description: 'For number questions' },
                                        { type: 'array', items: { type: 'string' }, description: 'For checkbox questions' }
                                    ]
                                }
                            }
                        }
                    }
                },
                steps: {
                    type: 'array',
                    description: 'Steps information',
                    items: {
                        type: 'object',
                        properties: {
                            id: { type: 'number' },
                            title: { type: 'string' },
                            order: { type: 'number' }
                        }
                    }
                },
                completion_date: {
                    type: 'string',
                    format: 'date-time'
                }
            }
        },
        knowledge_ids: {
            type: 'array',
            items: {
                type: 'string'
            }
        },
        avatar_config: {
            type: 'object',
            properties: {
                name: { type: 'string' },
                age: { type: 'string' },
                gender: {
                    type: 'string',
                    enum: ['male', 'female']
                },
                voice: { type: 'string' },
                description: { type: 'string' },
                selectedModel: {
                    type: 'object',
                    properties: {
                        model: {
                            type: 'object',
                            properties: {
                                label: { type: 'string' },
                                value: { type: 'string' }
                            }
                        },
                        cost: { type: 'number' }
                    }
                }
            }
        },
        logo: {
            type: 'object',
            description: 'Avatar image/logo file',
            properties: {
                id: { type: 'number' },
                filename: { type: 'string' },
                url: { type: 'string' }
            }
        },
        avatar_id: {
            type: 'string',
            description: 'ID of created avatar (returned after completion)'
        },
        created_at: {
            type: 'string',
            format: 'date-time'
        },
        updated_at: {
            type: 'string',
            format: 'date-time'
        }
    }
};
const OnboardingListResponse = {
    type: 'object',
    properties: {
        success: {
            type: 'boolean'
        },
        data: {
            type: 'array',
            items: OnboardingResponse
        },
        count: {
            type: 'number'
        }
    }
};
exports.onboardingOpenApiDocs = {
    'onboarding.list': {
        description: 'List all onboardings with optional search',
        responses: {
            200: {
                description: 'Onboardings retrieved successfully',
                returnParams: OnboardingListResponse
            }
        }
    },
    'onboarding.create': {
        description: `Create a new onboarding session with question answers.
        
**Answer Format by Question Type:**
- **text/textarea**: String value (e.g., "My answer")
- **number**: Numeric value (e.g., 25)
- **select**: String value of selected option (e.g., "option_value")
- **radio**: String value of selected option (e.g., "selected_value") 
- **checkbox**: Array of selected option values (e.g., ["option1", "option2"])

**Workflow:**
1. Get profession steps via \`onboarding.profession.steps\`
2. Present questions to user by step
3. Collect answers in \`data.answers\` object keyed by question ID
4. Submit onboarding with \`status: "completed"\` to auto-create avatar`,
        payload: {
            body: OnboardingCreatePayload
        },
        responses: {
            200: {
                description: 'Onboarding created successfully. If status is "completed", an avatar may be automatically created.',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: {
                            type: 'boolean',
                            example: true
                        },
                        data: OnboardingResponse
                    }
                }
            },
            400: {
                description: 'Bad request - invalid profession_id or missing required fields',
                returnParams: ErrorResponse
            }
        }
    },
    'onboarding.get': {
        description: 'Get onboarding by ID',
        responses: {
            200: {
                description: 'Onboarding retrieved successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: {
                            type: 'boolean',
                            example: true
                        },
                        data: OnboardingResponse
                    }
                }
            },
            404: {
                description: 'Onboarding not found',
                returnParams: ErrorResponse
            }
        }
    },
    'onboarding.update': {
        description: `Update onboarding session with question answers. When status is set to "completed", an avatar will be automatically created from the onboarding data.
        
**Answer Processing:**
- Answers are stored in \`data.answers\` object with question IDs as keys
- System creates \`data.structured_answers\` for easier processing (organized by step → question → answer details)
- Checkbox answers are stored as arrays, other types as strings/numbers
- Avatar description is enhanced with relevant answers when avatar is created

**Avatar Creation Trigger:**
- Occurs when \`status\` is set to \`"completed"\`
- Requires \`avatar_config.selectedModel\` to be present
- Uses answers to enhance avatar description
- Returns \`avatar_id\` in response when successful`,
        payload: {
            body: OnboardingUpdatePayload
        },
        responses: {
            200: {
                description: 'Onboarding updated successfully. Response includes avatar_id if avatar was created.',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: {
                            type: 'boolean',
                            example: true
                        },
                        data: OnboardingResponse
                    }
                }
            },
            404: {
                description: 'Onboarding not found',
                returnParams: ErrorResponse
            }
        }
    },
    'onboarding.delete': {
        description: 'Delete onboarding by ID',
        responses: {
            200: {
                description: 'Onboarding deleted successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: {
                            type: 'boolean',
                            example: true
                        },
                        data: OnboardingResponse
                    }
                }
            },
            404: {
                description: 'Onboarding not found',
                returnParams: ErrorResponse
            }
        }
    },
    'onboarding.profession.steps': {
        description: 'Get onboarding steps with questions for a specific profession',
        responses: {
            200: {
                description: 'Profession onboarding steps with questions retrieved successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: {
                            type: 'boolean',
                            example: true
                        },
                        data: {
                            type: 'array',
                            items: {
                                type: 'object',
                                properties: {
                                    id: {
                                        type: 'number'
                                    },
                                    title: {
                                        type: 'string'
                                    },
                                    description: {
                                        type: 'string'
                                    },
                                    order: {
                                        type: 'number'
                                    },
                                    questions: {
                                        type: 'array',
                                        description: 'Questions for this step',
                                        items: {
                                            type: 'object',
                                            properties: {
                                                id: { type: 'number' },
                                                title: { type: 'string' },
                                                description: { type: 'string' },
                                                type: {
                                                    type: 'string',
                                                    enum: ['text', 'textarea', 'number', 'select', 'radio', 'checkbox'],
                                                    description: 'Question type determines answer format'
                                                },
                                                required: { type: 'boolean' },
                                                order: { type: 'number' },
                                                options: {
                                                    type: 'array',
                                                    description: 'Available options for select, radio, or checkbox questions',
                                                    items: {
                                                        anyOf: [
                                                            { type: 'string' },
                                                            {
                                                                type: 'object',
                                                                properties: {
                                                                    value: { type: 'string' },
                                                                    label: { type: 'string' }
                                                                }
                                                            }
                                                        ]
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            404: {
                description: 'Profession not found',
                returnParams: ErrorResponse
            }
        }
    }
};
